import React from 'react';
import { Badge, Tooltip } from '@mui/material';
import BusinessIcon from '@mui/icons-material/Business';
import StarIcon from '@mui/icons-material/Star';
import StarHalfIcon from '@mui/icons-material/StarHalf';
import Filter5Icon from '@mui/icons-material/Filter5';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import SmsIcon from '@mui/icons-material/Sms';
import EmailIcon from '@mui/icons-material/Email';
import WorkIcon from '@mui/icons-material/Work';
import PersonIcon from '@mui/icons-material/Person';
import CategoryRoundedIcon from '@mui/icons-material/CategoryRounded';
import PhoneDisabledRoundedIcon from '@mui/icons-material/PhoneDisabledRounded';
import capitalize from '../../../Helpers/StringsFormatting/capitalize';
import Chip from '../../../Components/Chip';
import ActivityTypeAvatar from '../../../Components/ActivityTypeAvatar';
import { createNewDate, culture, options } from '../../../Helpers/dates';

export const MASSIVE_PROPERTIES = {
  TITLE: 'templateId',
  DATE: 'scheduledOn',
  CUSTOMERS_NUMBER: 'customersInvolved',
};

export const FILTERS_MODEL = {
  Branches: [],
  Sections: [],
  WithWave: true,
  WithLite: false,
  WithCorporate: false,
  WithPremium: false,
  Rating: [],
  IsSMS: false,
  Occupation: [],
  Products: [],
  CustomList: [],
  LowerAge: 18,
  UpperAge: 99,
  RemainingCustomers: false,
  WithoutPhoneNumber: false,
};
export const FILTERS_TERMS = {
  Branches: 'BRANCHES',
  Sections: 'SECTIONS',
  WithWave: 'CURRENT_WAVE',
  WithLite: 'LIGHT',
  WithCorporate: 'CORPORATE',
  WithPremium: 'PREMIUMBANK',
  Rating: 'RATING',
  CustomList: 'CUSTOMERS',
  IsSMS: 'CHANNEL',
  Occupation: 'OCCUPATION',
  LowerAge: 'LOWER_AGE',
  UpperAge: 'UPPER_AGE',
  RemainingCustomers: 'REMAINING_CUSTOMERS',
  Products: 'PRODUCTS',
  WithoutPhoneNumber: 'PHONE_UNAVAILABLE',
};

export const FILTERS_ICONS = {
  Branches: <BusinessIcon style={{ color: '#fd8858' }} />,
  Sections: null,
  WithWave: null,
  WithLite: <StarIcon color="primary" />,
  WithCorporate: <Filter5Icon style={{ color: '#49A600' }} />,
  WithPremium: <AccountBalanceIcon style={{ color: '#FFC700' }} />,
  Rating: <StarHalfIcon color="primary" />,
  IsSMS: {
    sms: <SmsIcon style={{ color: '#2c77c7' }} />,
    email: <EmailIcon style={{ color: '#b00020' }} />,
  },
  Occupation: <WorkIcon style={{ color: '#b5a2fe' }} />,
  LowerAge: null,
  UpperAge: null,
  RemainingCustomers: <PersonIcon />,
  Products: <CategoryRoundedIcon />,
  WithoutPhoneNumber: <PhoneDisabledRoundedIcon style={{ color: '#b00020' }} />,
  CustomList: <PersonIcon />,
};

export const SECTIONS_OPTIONS = [
  { label: 'PREMIUM BANK', value: 0, key: 'withPremium' },
  { label: 'CORPORATE', value: 1, key: 'withCorporate' },
  { label: 'LIGHT', value: 2, key: 'withLight' },
  { label: 'SPOT', value: 3, key: 'withSpot' },
];

export const WAVE = [
  {
    id: 0,
    label: 'CURRENT_WAVE',
  },
  {
    id: 1,
    label: 'NOT_CURRENT_WAVE',
  },
];

export const CHANNELS = [
  {
    id: 0,
    label: 'EMAIL',
  },
  {
    id: 1,
    label: 'SMS',
  },
];
export const TAG = 'MASSIVE-SMS';

const formatDate = (value) =>
  value ? createNewDate(value).toLocaleDateString(culture, options) : null;

//chip colors for sends counters and outcomes (shared with SendsAnalysis)
export const OUTCOME_COLORS = {
  CUSTOMERS: '#1D5B9D',
  SENT: '#0288d1',
  POSITIVE: '#34A853',
  NEGATIVE: '#b00020',
  WARNING: '#f9a825',
  CTA: '#9c27b0',
};

//shared by NEW and OLD campaign accordions
export const CAMPAIGN_ACCORDION_STYLES = {
  root: { '&::before': { opacity: 0.5 } }, //lighter separator between accordions
  summary: {
    pl: 0,
    '& .MuiAccordionSummary-content': { m: 0, minWidth: 0 },
  },
  summaryRow: { flexGrow: 1 },
  details: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    p: '0 16px 16px',
  },
  sendPaper: { overflow: 'hidden' },
};

//channel avatar with queued status dot, first column of a send row
export const getChannelColumn = (isSms, enqueued, intl) => ({
  id: 'channel',
  noCaption: true,
  size: 'auto',
  formatCallback: () => (
    <Tooltip
      arrow
      placement="top"
      title={enqueued ? intl.messages.QUEUED : intl.messages.QUEUING}
    >
      <Badge
        color={enqueued ? 'success' : 'warning'}
        variant="dot"
        overlap="circular"
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <ActivityTypeAvatar activityType={isSms ? 'SMS' : 'EMAIL'} />
      </Badge>
    </Tooltip>
  ),
});

export const counterChip = (value, color) => (
  <Chip label={String(value ?? 0)} color={color} width="fit-content" />
);

export const CAMPAIGN_COLUMNS = [
  {
    id: 'name',
    label: 'CAMPAIGNNAME',
    formatCallback: (value) => capitalize(value),
  },
  {
    id: 'templateName',
    label: 'TEMPLATE',
    size: 2.5,
    formatCallback: (value) => capitalize(value),
  },
  {
    id: 'scheduledOn',
    label: 'CAMPAIGN_START_DATE',
    size: 1.8,
    formatCallback: formatDate,
  },
  {
    id: 'endsOn',
    label: 'CAMPAIGN_END_DATE',
    size: 1.8,
    formatCallback: formatDate,
  },
  {
    id: 'customersInvolved',
    label: 'CUSTOMERS_INVOLVED',
    size: 1.5,
    formatCallback: (value) => counterChip(value, OUTCOME_COLORS.CUSTOMERS),
  },
];

export const CAMPAIGN_SORTING = [
  {
    label: 'SCHEDULEDON',
    value: 'scheduledOn',
    direction: 'desc',
    isDefault: true,
  },
  { label: 'CAMPAIGNNAME', value: 'name' },
];

export const CAMPAIGN_FILTERS = [{ keyName: 'branchId' }];

export const CAMPAIGN_RANGE_FILTERS = [
  { keyName: 'scheduledOn', includeExtremes: true },
];
